import { AUTHORITIES } from '../store/modules/auth';
import DateUtils from '../utils/dateUtils';
import { i18nGlobal } from '@/i18n';
import { Salutation } from './salutation';

export interface Trainer {
  id?: string;
  logo: string;
  logoId?: string;
  responsibleId?: string;
  firstName: string;
  lastName: string;
  salutation: Salutation;
  userName: string;
  locations: string;
  departmentNames: string;
  skillNames: string;
  title: string;
  email: string;
  phone: string;
  text: string;
  employeeNo: string;
  zip: string;
  street: string;
  city: string;
  isRegistrated: boolean;
  isOnline: boolean;
  isIntern: boolean;
  authorities: string[];
  readonly authoritiesString: string;
  active: boolean;
  fromDate: string;
  tillDate: string;
  birthday: string;
  lastLoginDate: string;
  createdDate: string;
  readonly isInactiveOrExpired: boolean;
  readonly fullName: string;
  unlocked: boolean;
  feeTotalCurrentYear: number;
  feeLimitCurrentYear: number | null | undefined;
}

export interface Value {
  value: Trainer[];
  '@odata.context'?: string | undefined;
  '@odata.count'?: number | undefined;
}

// init api object
function toAPI(data: Partial<Trainer>): any {
  return {
    Id: data?.id || undefined,
    ResponsibleId: data?.responsibleId || undefined,
    FirstName: data?.firstName || '',
    LastName: data?.lastName || '',
    Salutation: data?.salutation || '',
    UserName: data?.userName || '',
    Title: data?.title || '',
    Email: data?.email || '',
    Phone: data?.phone || '',
    Text: data?.text || '',
    EmployeeNo: data?.employeeNo || '',
    Zip: data?.zip || '',
    Street: data?.street || '',
    City: data?.city || '',
    IsRegistrated: data?.isRegistrated || false,
    Authorities: data?.authorities?.join(',') || '',
    IsIntern: data?.isIntern || false,
    Active: data?.active || false,
    FromDate: data?.fromDate || '',
    TillDate: DateUtils.dateNullableToApi(data?.tillDate),
    Birthday: DateUtils.dateNullableToApi(data?.birthday),
    Unlocked: data?.unlocked ?? false,
  };
}

function parse(data?: Partial<Trainer>): Trainer {
  return {
    id: data?.id || undefined,
    logo: data?.logo || '',
    logoId: data?.logoId || '',
    responsibleId: data?.responsibleId || undefined,
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    salutation: data?.salutation || '',
    title: data?.title || '',
    phone: data?.phone || '',
    email: data?.email || '',
    userName: data?.userName || '',
    locations: data?.locations || '',
    departmentNames: data?.departmentNames || '',
    skillNames: data?.skillNames || '',
    text: data?.text || '',
    employeeNo: data?.employeeNo || '',
    zip: data?.zip || '',
    street: data?.street || '',
    city: data?.city || '',
    isRegistrated: data?.isRegistrated || false,
    isOnline: data?.isOnline || false,
    isIntern: data?.isIntern !== undefined ? data.isIntern : true,
    authorities: data?.authorities || [],
    get authoritiesString() {
      return this.authorities
        .map((x) => i18nGlobal.t(`app.authorities.${x}`))
        .join(', ');
    },
    active: data?.active || false,
    fromDate: DateUtils.parseDateFromBackend(
      data?.fromDate,
      DateUtils.getTodayDateAsIsoString()
    ),
    tillDate: DateUtils.parseDateFromBackend(data?.tillDate),
    birthday: DateUtils.parseDateFromBackend(data?.birthday),
    createdDate: data?.createdDate || '',
    lastLoginDate: data?.lastLoginDate || '',
    get isInactiveOrExpired(): boolean {
      let todayDate = DateUtils.getTodayDateAsIsoString();
      return (
        !this.active ||
        (this.tillDate != '' && this.tillDate < todayDate) ||
        this.fromDate > todayDate
      );
    },
    get fullName() {
      return `${this.lastName}, ${this.firstName}`;
    },
    unlocked: data?.unlocked ?? false,
    feeTotalCurrentYear: data?.feeTotalCurrentYear || 0,
    feeLimitCurrentYear: data?.feeLimitCurrentYear ?? null,
  };
}

export const EmptyTrainer: Trainer = parse({
  birthday: DateUtils.getTodayDateAsIsoString(),
  authorities: [AUTHORITIES.TRAINER],
});

export default {
  parse,
  toAPI: toAPI,
};

export interface TrainerSkillsCoverage {
  trainerId: string;
  percentage: number;
}
